import { Allocation, OwnerType } from "@/api/SolarCloudApi";
import { usePersonalAllocations } from "@/queries/allocations/usePersonalAllocations";
import { useUpdatePersonalAllocations } from "@/queries/allocations/useUpdatePersonalAllocations";
import { useCallback, useMemo } from "react";

interface UseAllocationsProps {
  userId?: string;
  account?: "gas" | "electricity" | "bank" | undefined;
}

interface UseAllocationsReturn {
  allocations: Allocation[] | undefined;
  totalAllocated: number;
  addAllocation: (allocation: Allocation) => void;
  deleteAllocation: (allocation: Allocation) => void;
  updateAllocation: (allocation: Allocation) => void;
  moveAllocation: (allocation: Allocation, direction: "up" | "down") => void;
  isLoading: boolean;
  error: Error | null;
}

export function useAllocations({
  userId,
  account,
}: UseAllocationsProps): UseAllocationsReturn {
  const { data: allocations, isLoading, error } = usePersonalAllocations();
  const { mutate: updateAllocations } = useUpdatePersonalAllocations();

  const addAllocation = useCallback(
    (allocation: Allocation) => {
      if (!allocations) return;

      const allocationWithOwner = {
        ...allocation,
        owner: {
          type: OwnerType.USER,
          user: {
            uuid: userId!,
          },
        },
      };

      const newAllocations = [...allocations, allocationWithOwner];
      updateAllocations(newAllocations);
    },
    [allocations, userId, updateAllocations]
  );

  const deleteAllocation = useCallback(
    (allocation: Allocation) => {
      if (!allocations) return;

      const updatedAllocations = allocations.filter(
        (a) => a.uuid !== allocation.uuid
      );
      updateAllocations(updatedAllocations);
    },
    [allocations, updateAllocations]
  );

  const updateAllocation = useCallback(
    (allocation: Allocation) => {
      if (!allocations) return;

      const updatedAllocations = allocations.map((a) =>
        a.uuid === allocation.uuid
          ? {
              ...a,
              ...allocation,
            }
          : a
      );
      updateAllocations(updatedAllocations);
    },
    [allocations, updateAllocations]
  );

  // const moveAllocation = useCallback(
  //   (allocation: Allocation, direction: "up" | "down") => {
  //     if (!allocations) return;

  //     const index = allocations.findIndex((a) => a.uuid === allocation.uuid);
  //     if (index === -1) return;

  //     const newAllocations = [...allocations];
  //     if (direction === "up" && index > 0) {
  //       [newAllocations[index - 1], newAllocations[index]] = [
  //         newAllocations[index],
  //         newAllocations[index - 1],
  //       ];
  //     } else if (direction === "down" && index < newAllocations.length - 1) {
  //       [newAllocations[index + 1], newAllocations[index]] = [
  //         newAllocations[index],
  //         newAllocations[index + 1],
  //       ];
  //     }
  //     updateAllocations(newAllocations);
  //   },
  //   [allocations, updateAllocations]
  // );
  const moveAllocation = useCallback(
    (allocation: Allocation, direction: "up" | "down") => {
      if (!allocations) return;

      const matchesAccount = (a: Allocation) => {
        if (account === "gas") {
          return a.nickname?.startsWith("::gas::");
        }
        if (account === "electricity") {
          return !a.bankPayee && !a.nickname?.startsWith("::gas::");
        }
        if (account === "bank") {
          return a.bankPayee !== null && a.bankPayee !== undefined;
        }
        return false;
      };

      // subset of allocations relevant to current account
      const filtered = allocations.filter(matchesAccount);
      const index = filtered.findIndex((a) => a.uuid === allocation.uuid);
      if (index === -1) return;

      const newFiltered = [...filtered];
      if (direction === "up" && index > 0) {
        [newFiltered[index - 1], newFiltered[index]] = [
          newFiltered[index],
          newFiltered[index - 1],
        ];
      } else if (direction === "down" && index < newFiltered.length - 1) {
        [newFiltered[index + 1], newFiltered[index]] = [
          newFiltered[index],
          newFiltered[index + 1],
        ];
      }

      // Now merge back into original allocations, preserving the other ones
      const newAllocations = allocations.map((a) => {
        if (!matchesAccount(a)) return a; // untouched
        // shift next replacement from newFiltered
        return newFiltered.shift()!;
      });

      updateAllocations(newAllocations);
    },
    [allocations, account, updateAllocations]
  );

  const totalAllocated = useMemo(
    () =>
      allocations?.reduce(
        (total, allocation) => total + (allocation.wattCapacity || 0),
        0
      ) || 0,
    [allocations]
  );

  return {
    allocations,
    totalAllocated,
    addAllocation,
    deleteAllocation,
    updateAllocation,
    moveAllocation,
    isLoading,
    error: error as Error | null,
  };
}
